/**
 * CreateOrderPage
 * Dedicated page for creating new orders
 * Integrates with order creation form/dialog
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-001
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { CreateOrderDialog } from '@/modules/sales/components/CreateOrderDialog';
import { useState } from 'react';

/**
 * CreateOrderPage Component
 * Provides interface for creating new orders
 */
export default function CreateOrderPage() {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(true);

  /**
   * Handle back navigation
   */
  function handleBack() {
    navigate('/sales/orders');
  }

  /**
   * Handle order created success
   */
  async function handleOrderSuccess(orderData?: any) {
    // Navigate back to orders list after successful creation
    navigate('/sales/orders');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 flex items-center gap-4"
        >
          <Button
            onClick={handleBack}
            variant="outline"
            size="icon"
            className="rounded-full border-slate-300 bg-white hover:bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Create New Order</h1>
            <p className="text-sm text-slate-500 mt-1">
              Create a new purchase order from an active contract
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <CreateOrderDialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              // If dialog closes, navigate back
              if (!open) {
                navigate('/sales/orders');
              }
            }}
            onSuccess={handleOrderSuccess}
          />
        </motion.div>
      </div>
    </div>
  );
}
